// Composant de routage dynamique pour les services
import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useTenantContext } from '../../context/TenantContext';
import { Loader2 } from 'lucide-react';
import { getServiceImports, getServiceFiles } from '../../utils/autoServiceScanner';

// Composant de chargement
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-black">
    <div className="text-center">
      <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
      <p className="text-white/80">Chargement du service...</p>
    </div>
  </div>
);

export const DynamicServiceRouter: React.FC = () => {
  const { tenant } = useTenantContext();

  // Récupérer la configuration des services du tenant
  const servicesConfig = tenant?.config?.services;
  const folder = servicesConfig?.folder;
  const customRoutes = servicesConfig?.routes || {};

  // Si aucun dossier configuré, ne rien afficher
  if (!folder) {
    return null;
  }

  // Récupérer les imports et fichiers disponibles automatiquement
  const serviceImports = getServiceImports();
  const availableServices = serviceImports[folder];
  const serviceFiles = getServiceFiles(folder);

  // Si le dossier n'existe pas ou est vide
  if (!availableServices || Object.keys(availableServices).length === 0) {
    return null;
  }

  // Générer les routes dynamiques
  const routes = serviceFiles.map((file) => {
    // Utiliser la route personnalisée ou la route par défaut
    const routePath = customRoutes[file.key] || file.defaultRoute;
    
    // Récupérer la fonction d'import
    const importFn = availableServices[file.key];
    
    if (!importFn) return null;

    // Créer le composant lazy
    const LazyComponent = React.lazy(importFn);

    return (
      <Route
        key={file.key}
        path={routePath}
        element={
          <Suspense fallback={<LoadingFallback />}>
            <LazyComponent />
          </Suspense>
        }
      />
    );
  }).filter(Boolean);

  return <Routes>{routes}</Routes>;
};

export default DynamicServiceRouter;
